/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";

import breakpoints from "assets/theme/base/breakpoints";

// Data
import ShortListTutorsTableData from "Views/Activities/ShortListTutors/data/ShortListTableData";
import {
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import React, { useState, useEffect, useRef } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import api from "utils/api";
import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";
import { CircularProgress, Alert, Snackbar } from "@mui/material";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import defaultAvatar from "assets/images/team-2.jpg";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

// Google Maps API Key - Replace with your own key
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";

function Tables() {
  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { columns, rows } = ShortListTutorsTableData;

  const [Enquiry, setEnquiry] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [Status, setStatus] = useState("");
  const [Academic, setAcademic] = useState([]);
  const [Gender, setGender] = useState("");
  const [BadgeLevel, setBadgeLevel] = useState([]);
  const [fluency, setFluency] = useState(0);
  const [radius, setRadius] = useState(0);
  const [parents, setParents] = useState([]);
  const [parentDetails, setParentDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTutorIds, setSelectedTutorIds] = useState([]);
  const [savingShortlist, setSavingShortlist] = useState(false);

  // Map states
  const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.209 }); // Default to Delhi
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapZoom, setMapZoom] = useState(13);
  const mapRef = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const response = await api.get("/parent");
        if (response.data.success) {
          const parentOptions = response.data.data.map((parent) => ({
            label: parent.fullName,
            value: parent._id,
            location: parent.location,
          }));
          setParents([{ label: "Select Parent", value: "" }, ...parentOptions]);
        }
      } catch (error) {
        console.error("Error fetching parents:", error);
      }
    };
    fetchParents();
  }, []);

  // Fetch parent details and update map when parent is selected
  useEffect(() => {
    const fetchParentDetails = async () => {
      if (!Enquiry || Enquiry === "") {
        setParentDetails(null);
        setMapCenter({ lat: 28.6139, lng: 77.209 });
        setSelectedLocation(null);
        return;
      }

      try {
        const response = await api.get(`/parent?id=${Enquiry}`);

        if (response.data.success) {
          const parent = response.data.data;
          setParentDetails(parent);

          // Set map center and marker to parent's location
          // Database format: [latitude, longitude]
          if (
            parent &&
            parent.location &&
            parent.location.coordinates &&
            parent.location.coordinates.length === 2
          ) {
            const [lat, lng] = parent.location.coordinates;
            if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
              const newCenter = { lat, lng };
              setMapCenter(newCenter);
              setSelectedLocation(newCenter);
            } else {
              console.warn("Invalid coordinates for parent:", parent.location.coordinates);
            }
          } else {
            console.warn("Parent location not available or invalid:", parent?.location);
          }
        }
      } catch (error) {
        console.error("Error fetching parent details:", error);
      }
    };
    fetchParentDetails();
  }, [Enquiry]);

  // Fetch students when parent is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!Enquiry) {
        setStudents([]);
        setSelectedStudents([]);
        setSubjects([]);
        return;
      }

      try {
        const response = await api.get(`/student?parentId=${Enquiry}`);
        if (response.data.success) {
          const studentOptions = response.data.data.map((student) => ({
            label: student.name,
            value: student._id,
            ...student,
          }));
          setStudents(studentOptions);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };
    fetchStudents();
  }, [Enquiry]);

  // Fetch subjects when students are selected
  useEffect(() => {
    const fetchSubjectsForStudents = async () => {
      if (!selectedStudents || selectedStudents.length === 0) {
        setSubjects([]);
        setAcademic([]);
        return;
      }

      try {
        // Collect unique subjects from all selected students
        const allSubjects = new Map();

        for (const studentId of selectedStudents) {
          const response = await api.get(`/student?id=${studentId}`);
          if (response.data.success) {
            const studentData = response.data.data;
            if (studentData.subjects && Array.isArray(studentData.subjects)) {
              studentData.subjects.forEach((subject) => {
                if (subject._id && subject.name) {
                  allSubjects.set(subject._id, {
                    label: subject.name,
                    value: subject._id,
                  });
                }
              });
            }
          }
        }

        const subjectOptions = Array.from(allSubjects.values());
        setSubjects(subjectOptions);
      } catch (error) {
        console.error("Error fetching student details:", error);
        setSubjects([]);
      }
    };
    fetchSubjectsForStudents();
  }, [selectedStudents]);

  // Apply filters and fetch tutors
  const handleApplyFilters = async () => {
    if (!Enquiry || !selectedStudents || selectedStudents.length === 0) {
      toast.error("Please select a parent and at least one student");
      return;
    }

    // Validate that parent has location if radius is being used
    if (radius > 0 && !selectedLocation) {
      toast.error("Parent location not available. Cannot apply radius filter.");
      return;
    }

    setLoading(true);

    try {
      // Fetch tutors for each student and combine results
      const allTutorsMap = new Map();

      for (const studentId of selectedStudents) {
        const selectedStudent = students.find((student) => student?.value === studentId);
        const preferredDurationMinutes = Number(selectedStudent?.preferredTimeDuration || 0);
        const preferredClassesPerWeek = Number(selectedStudent?.noOfClassesPerWeek || 0);

        const params = {
          parentId: Enquiry,
          studentId: studentId,
          page: 1,
          limit: 100,
        };

        if (preferredDurationMinutes > 0) {
          params.duration = preferredDurationMinutes;
        }

        if (preferredClassesPerWeek > 0) {
          params.teachingDaysPerWeek = preferredClassesPerWeek;
        }

        // Subject filter - make sure it's a valid string
        if (Academic && Array.isArray(Academic) && Academic.length > 0) {
          params.subjects = Academic.join(",");
        }

        // Gender filter - make sure it's a valid value
        if (Gender && Gender !== "" && Gender !== "Select Gender") {
          params.gender = Gender.trim();
        }

        // Badge/Level filter - handle multiple levels
        if (BadgeLevel && Array.isArray(BadgeLevel) && BadgeLevel.length > 0) {
          // Send as comma-separated string
          params.tutorLevel = BadgeLevel.join(",");
        }

        // English proficiency filter
        if (fluency && parseInt(fluency) > 0) {
          params.englishProficiency = parseInt(fluency);
        }

        // Use parent's location for radius filtering
        if (radius && parseFloat(radius) > 0 && selectedLocation) {
          params.radius = parseFloat(radius);
          params.lat = parseFloat(selectedLocation.lat);
          params.long = parseFloat(selectedLocation.lng);
        }

        console.log("🔍 Sending filter params for student:", studentId, params);

        const response = await api.get("/tutor/filter-for-parent", { params });

        console.log("📦 Response received:", response.data);

        if (response.data.success && response.data.data) {
          response.data.data.forEach((tutor) => {
            // Use tutorId if available, otherwise use _id
            const tutorKey = tutor.tutorId || tutor._id;
            if (!allTutorsMap.has(tutorKey)) {
              allTutorsMap.set(tutorKey, {
                ...tutor,
                _id: tutorKey, // Ensure _id is set for table display
              });
            }
          });
        } else {
          console.warn("⚠️ Invalid response format or no data:", response.data);
        }
      }

      const uniqueTutors = Array.from(allTutorsMap.values());

      // Sort by distance if radius was applied
      if (radius > 0 && selectedLocation) {
        uniqueTutors.sort((a, b) => {
          const distA = a.distance || Infinity;
          const distB = b.distance || Infinity;
          return distA - distB;
        });
      }

      setFilteredTutors(uniqueTutors);
      setSelectedTutorIds([]); // Clear selection when new filters are applied

      // Adjust map to show all markers
      if (uniqueTutors.length > 0 && selectedLocation) {
        // Collect all valid coordinates
        const allLocations = [selectedLocation];

        uniqueTutors.forEach((tutor) => {
          if (tutor.location?.coordinates?.length === 2) {
            const val1 = parseFloat(tutor.location.coordinates[0]);
            const val2 = parseFloat(tutor.location.coordinates[1]);

            if (!isNaN(val1) && !isNaN(val2)) {
              // Auto-detect correct lat/lng
              let lat, lng;
              if (val1 >= 8 && val1 <= 35 && val2 >= 68 && val2 <= 97) {
                lat = val1;
                lng = val2;
              } else if (val2 >= 8 && val2 <= 35 && val1 >= 68 && val1 <= 97) {
                lat = val2;
                lng = val1;
              } else {
                return; // Skip invalid coordinates
              }
              allLocations.push({ lat, lng });
            }
          }
        });

        if (allLocations.length > 1) {
          // Calculate bounds
          const lats = allLocations.map((loc) => loc.lat);
          const lngs = allLocations.map((loc) => loc.lng);

          const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
          const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

          setMapCenter({ lat: centerLat, lng: centerLng });

          // Calculate appropriate zoom level based on distance
          const latDiff = Math.max(...lats) - Math.min(...lats);
          const lngDiff = Math.max(...lngs) - Math.min(...lngs);
          const maxDiff = Math.max(latDiff, lngDiff);

          let zoom = 13;
          if (maxDiff > 0.5) zoom = 10;
          else if (maxDiff > 0.2) zoom = 11;
          else if (maxDiff > 0.1) zoom = 12;
          else if (maxDiff > 0.05) zoom = 13;
          else zoom = 14;

          setMapZoom(zoom);
        }
      }

      if (uniqueTutors.length === 0) {
        toast.info(
          "No tutors found matching your criteria. Try increasing the radius or adjusting filters."
        );
      }
    } catch (error) {
      console.error("❌ Error filtering tutors:", error);
      toast.error(error.response?.data?.message || "Error filtering tutors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox selection
  const handleTutorSelection = (tutorId) => {
    setSelectedTutorIds((prev) => {
      if (prev.includes(tutorId)) {
        return prev.filter((id) => id !== tutorId);
      } else {
        return [...prev, tutorId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allTutorIds = filteredTutors.map((tutor) => tutor._id);
      setSelectedTutorIds(allTutorIds);
    } else {
      setSelectedTutorIds([]);
    }
  };

  // Handle save shortlisted tutors
  const handleSaveShortlist = async () => {
    if (!Enquiry) {
      toast.error("Please select a parent first");
      return;
    }

    if (selectedTutorIds.length === 0) {
      toast.error("Please select at least one tutor to shortlist");
      return;
    }

    setSavingShortlist(true);

    try {
      const response = await api.post("/shortlisted-tutors/save", {
        parentId: Enquiry,
        tutorIds: selectedTutorIds,
        studentIds: selectedStudents,
      });

      if (response.data.success) {
        toast.success(
          `${response.data.message}${
            response.data.data?.duplicates
              ? ` (${response.data.data.duplicates.length} already shortlisted)`
              : ""
          }`
        );
        setSelectedTutorIds([]); // Clear selection after successful save
      }
    } catch (error) {
      console.error("Error saving shortlisted tutors:", error);
      toast.error(error.response?.data?.message || "Error saving shortlisted tutors");
    } finally {
      setSavingShortlist(false);
    }
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setEnquiry("");
    setSelectedStudents([]);
    setStatus("");
    setAcademic([]);
    setGender("");
    setBadgeLevel("");
    setFluency(0);
    setRadius(0);
    setFilteredTutors([]);
    setSelectedTutorIds([]);
    setParentDetails(null);
    setStudents([]);
    setSubjects([]);
    setMapCenter({ lat: 28.6139, lng: 77.209 });
    setSelectedLocation(null);
    setMapZoom(13);
    toast.info("Filters reset successfully");
  };

  // Format tutors data for table display
  const formatTutorsForTable = () => {
    return filteredTutors.map((tutor, index) => {
      // Calculate age from DOB if available
      let age = "N/A";
      if (tutor.dob) {
        const birthDate = new Date(tutor.dob);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
      }

      // Badge color mapping
      const badgeColors = {
        "Level 1": "error",
        "Level 2": "warning",
        "Level 3": "success",
        "Level 4": "info",
      };

      // Badge emoji mapping
      const badgeEmojis = {
        "Level 1": "🥉",
        "Level 2": "🥈",
        "Level 3": "🥇",
        "Level 4": "💎",
      };

      return {
        "S.No.": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {index + 1}
          </ArgonTypography>
        ),
        Tutor: (
          <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
            <ArgonBox mr={2}>
              <ArgonAvatar src={tutor.profileImage} alt={tutor.fullName} size="sm" variant="full" />
            </ArgonBox>
            <ArgonBox display="flex" flexDirection="column">
              <ArgonTypography variant="button" fontWeight="medium">
                <Link
                  to={`/tutors/profile/${tutor._id}`}
                  style={{ textDecoration: "underline", color: "inherit" }}
                >
                  {tutor.fullName}
                </Link>
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary">
                {tutor.email || "No email"}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
        "Mobile No.": (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {tutor.phone}
          </ArgonTypography>
        ),
        Age: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {age}
          </ArgonTypography>
        ),
        Distance: (
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {tutor.distance ? `${tutor.distance.toFixed(1)} km` : "N/A"}
          </ArgonTypography>
        ),
        Badge: tutor.tutorLevel ? (
          <ArgonBadge
            variant="gradient"
            badgeContent={`${badgeEmojis[tutor.tutorLevel]} ${tutor.tutorLevel}`}
            color={badgeColors[tutor.tutorLevel]}
            size="xs"
            container
          />
        ) : (
          <ArgonTypography variant="caption" color="text">
            No Level
          </ArgonTypography>
        ),
        "Per Session": (
          <ArgonBox display="flex" flexDirection="column" alignItems="center">
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              ₹{tutor.perSessionParentFee || tutor.hourlyRate || 0}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              {tutor.requestedDurationMinutes
                ? `${tutor.requestedDurationMinutes} mins`
                : tutor.requestedDuration || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        Select: (
          <Checkbox
            checked={selectedTutorIds.includes(tutor._id)}
            onChange={() => handleTutorSelection(tutor._id)}
            color="primary"
          />
        ),
      };
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            {/* Header */}
            <CardHeader
              title={
                <ArgonTypography variant="body2" fontWeight="medium">
                  Shortlist Tutors
                </ArgonTypography>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />

            <CardContent>
              <Grid container spacing={3}>
                {/* Left Side - Form */}
                <Grid item xs={12} md={6}>
                  <form>
                    <Grid container spacing={2}>
                      {/* Enquiry */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Parent *
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Enquiry}
                          onChange={(e) => setEnquiry(e.target.value)}
                          options={parents}
                          width={260}
                        />
                      </Grid>

                      {/* Student */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text" mb={1}>
                          Students *
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={students}
                          getOptionLabel={(option) => option.label || option.name || ""}
                          value={students.filter((student) =>
                            selectedStudents.includes(student.value)
                          )}
                          onChange={(event, newValue) => {
                            const selectedIds = newValue.map((student) => student.value);
                            setSelectedStudents(selectedIds);
                          }}
                          disabled={!Enquiry || students.length === 0}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label || option.name}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                students.length === 0 ? "No students available" : "Select students"
                              }
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {/* Status */}
                      {/* <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Status
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Status}
                          onChange={(e) => setStatus(e.target.value)}
                          options={["Status 1", "Status 2", "Status 3"]}
                          width={260}
                        />
                      </Grid> */}

                      {/* Subject */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Subject (Optional)
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={subjects}
                          getOptionLabel={(option) => option.label || ""}
                          value={subjects.filter((subject) => Academic.includes(subject.value))}
                          onChange={(event, newValue) => {
                            const selectedSubjectIds = newValue.map((subject) => subject.value);
                            setAcademic(selectedSubjectIds);
                          }}
                          disabled={!selectedStudents || selectedStudents.length === 0}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                subjects.length === 0 ? "No subjects" : "Select subjects"
                              }
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text">
                          Gender
                        </ArgonTypography>
                        <Dropdown
                          label=""
                          value={Gender}
                          onChange={(e) => setGender(e.target.value)}
                          options={[
                            { label: "Select Gender", value: "" },
                            { label: "Male", value: "MALE" },
                            { label: "Female", value: "FEMALE" },
                            { label: "Both", value: "BOTH" },
                            { label: "Other", value: "OTHER" },
                          ]}
                          width={260}
                        />
                      </Grid>

                      {/* Badge/Level */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="button" fontWeight="regular" color="text" mb={1}>
                          Level/Badge (Multi-select)
                        </ArgonTypography>
                        <Autocomplete
                          multiple
                          options={[
                            { label: "🥉 Level 1", value: "Level 1" },
                            { label: "🥈 Level 2", value: "Level 2" },
                            { label: "🥇 Level 3", value: "Level 3" },
                            { label: "💎 Level 4", value: "Level 4" },
                          ]}
                          getOptionLabel={(option) => option.label}
                          value={[
                            { label: "🥉 Level 1", value: "Level 1" },
                            { label: "🥈 Level 2", value: "Level 2" },
                            { label: "🥇 Level 3", value: "Level 3" },
                            { label: "💎 Level 4", value: "Level 4" },
                          ].filter((option) => BadgeLevel.includes(option.value))}
                          onChange={(event, newValue) => {
                            const selectedLevels = newValue.map((level) => level.value);
                            setBadgeLevel(selectedLevels);
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option.label}
                                {...getTagProps({ index })}
                                key={option.value}
                                size="small"
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select levels"
                              size="small"
                            />
                          )}
                          sx={{
                            width: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Grid>

                      {/* English Fluency */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          English Fluency ({fluency})
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={10}
                          value={fluency}
                          onChange={(e, newValue) => setFluency(newValue)}
                        />
                      </Grid>

                      {/* Radius */}
                      <Grid item xs={12} sm={6}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Radius ({radius} Km)
                        </ArgonTypography>
                        <Slider
                          sx={{
                            "& .MuiSlider-track": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-thumb": { backgroundColor: "#1498fcff" },
                            "& .MuiSlider-rail": { backgroundColor: "#e0e0e0" },
                          }}
                          min={0}
                          max={10}
                          value={radius}
                          onChange={(e, newValue) => setRadius(newValue)}
                        />
                      </Grid>

                      {/* Submit */}
                      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        <ArgonButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={handleApplyFilters}
                          disabled={
                            loading ||
                            !Enquiry ||
                            !selectedStudents ||
                            selectedStudents.length === 0
                          }
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "Apply Filters"
                          )}
                        </ArgonButton>
                        <ArgonButton
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleResetFilters}
                          disabled={loading}
                        >
                          Reset Filters
                        </ArgonButton>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>

                {/* Right Side - Map */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="h6" mb={2}>
                    Parent Location{" "}
                    {parentDetails?.fullName && (
                      <span>
                        (
                        <Link
                          to={`/parents/profile/${Enquiry}`}
                          style={{ textDecoration: "underline", color: "inherit" }}
                        >
                          {parentDetails.fullName}
                        </Link>
                        )
                      </span>
                    )}
                  </ArgonTypography>
                  {!Enquiry || Enquiry === "" ? (
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="400px"
                      bgcolor="#f5f5f5"
                      borderRadius="12px"
                    >
                      <ArgonTypography variant="body2" color="text" textAlign="center">
                        Please select a parent to view location
                      </ArgonTypography>
                    </ArgonBox>
                  ) : (
                    <div style={{ height: "400px", borderRadius: "12px", overflow: "hidden" }}>
                      {loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                          p={3}
                        >
                          <ArgonTypography variant="body2" color="error" textAlign="center">
                            Map cannot be loaded. Please check your internet connection.
                          </ArgonTypography>
                        </ArgonBox>
                      )}
                      {!isLoaded && !loadError && (
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="100%"
                          bgcolor="#f5f5f5"
                        >
                          <CircularProgress size={40} />
                        </ArgonBox>
                      )}
                      {isLoaded && !loadError && (
                        <GoogleMap
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          center={mapCenter}
                          zoom={mapZoom}
                          onLoad={(map) => {
                            mapRef.current = map;
                          }}
                          options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            draggable: true,
                            zoomControl: true,
                            scrollwheel: true,
                          }}
                        >
                          {/* Parent Location Marker */}
                          {selectedLocation && (
                            <>
                              <OverlayView
                                position={selectedLocation}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    transform: "translate(-50%, -100%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={parentDetails?.profileImage || defaultAvatar}
                                    alt={parentDetails?.fullName}
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      borderRadius: "50%",
                                      border: "3px solid #FF0000",
                                      objectFit: "cover",
                                      backgroundColor: "white",
                                      cursor: "default",
                                    }}
                                  />
                                  <div
                                    style={{
                                      marginTop: "5px",
                                      backgroundColor: "white",
                                      padding: "8px 12px",
                                      borderRadius: "10px",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                      whiteSpace: "nowrap",
                                      textAlign: "center",
                                      minWidth: "120px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: "12px",
                                        color: "#FF0000",
                                      }}
                                    >
                                      {parentDetails?.fullName || "Parent"}
                                    </div>

                                    {/* Show parent area (use explicit field or extract from address) */}
                                    {(() => {
                                      const areaField = parentDetails?.area;
                                      const address = parentDetails?.address || "";
                                      const areaMatch = address.match(/Area:\s*([^,]+)/);
                                      const area =
                                        areaField || (areaMatch ? areaMatch[1].trim() : null);
                                      return area ? (
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#666",
                                            marginTop: "4px",
                                          }}
                                        >
                                          {area}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                              </OverlayView>
                            </>
                          )}

                          {/* Tutor Location Markers */}
                          {filteredTutors.map((tutor) => {
                            // Check if tutor has location data
                            if (!tutor.location || !tutor.location.coordinates) {
                              return null;
                            }

                            const coords = tutor.location.coordinates;

                            // Validate coordinates array
                            if (!Array.isArray(coords) || coords.length !== 2) {
                              return null;
                            }

                            // Extract values
                            const val1 = parseFloat(coords[0]);
                            const val2 = parseFloat(coords[1]);

                            // Validate numeric values
                            if (isNaN(val1) || isNaN(val2)) {
                              return null;
                            }

                            // Auto-detect correct lat/lng based on typical ranges
                            // For India: Lat ~8-35, Lng ~68-97
                            let lat, lng;

                            if (val1 >= 8 && val1 <= 35 && val2 >= 68 && val2 <= 97) {
                              lat = val1;
                              lng = val2;
                            } else if (val2 >= 8 && val2 <= 35 && val1 >= 68 && val1 <= 97) {
                              lat = val2;
                              lng = val1;
                            } else {
                              return null;
                            }

                            return (
                              <React.Fragment key={tutor._id}>
                                <OverlayView
                                  position={{ lat, lng }}
                                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      transform: "translate(-50%, -100%)",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={tutor.profileImage || defaultAvatar}
                                      alt={tutor.fullName}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        borderRadius: "50%",
                                        border: "3px solid #1498FC",
                                        objectFit: "cover",
                                        backgroundColor: "white",
                                        cursor: "default",
                                      }}
                                    />
                                    <div
                                      style={{
                                        marginTop: "5px",
                                        backgroundColor: "white",
                                        padding: "8px 12px",
                                        borderRadius: "10px",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        minWidth: "120px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "bold",
                                          fontSize: "12px",
                                          color: "#1498FC",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        {tutor.fullName || "Tutor"}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#7b809a",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        {tutor.distance ? `${tutor.distance.toFixed(1)} km` : "N/A"}
                                      </div>
                                      {tutor.address &&
                                        (() => {
                                          const areaMatch = tutor.address.match(/Area:\s*([^,]+)/);
                                          const area = areaMatch ? areaMatch[1].trim() : null;
                                          return area ? (
                                            <div
                                              style={{
                                                fontSize: "10px",
                                                color: "#666",
                                              }}
                                            >
                                              {area}
                                            </div>
                                          ) : null;
                                        })()}
                                    </div>
                                  </div>
                                </OverlayView>
                              </React.Fragment>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  )}
                  {selectedLocation && parentDetails && (
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="body2" color="text">
                        {parentDetails.area && `${parentDetails.area}, `}
                        {parentDetails.city}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="secondary">
                        Coordinates: {selectedLocation.lat.toFixed(6)},{" "}
                        {selectedLocation.lng.toFixed(6)}
                      </ArgonTypography>
                    </ArgonBox>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </ArgonBox>

        {/* Table Section */}
        <ArgonBox mt={3} p={3}>
          <Card sx={{ boxShadow: 6, borderRadius: "20px" }}>
            <CardHeader
              title={
                <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Filtered Tutors ({filteredTutors.length})
                  </ArgonTypography>
                  {filteredTutors.length > 0 && (
                    <ArgonButton
                      variant="gradient"
                      color="success"
                      size="small"
                      onClick={handleSaveShortlist}
                      disabled={savingShortlist || selectedTutorIds.length === 0}
                    >
                      {savingShortlist ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        `Shortlist ${
                          selectedTutorIds.length > 0 ? `(${selectedTutorIds.length})` : ""
                        } Tutors`
                      )}
                    </ArgonButton>
                  )}
                </ArgonBox>
              }
              sx={{ borderBottom: "1px solid #eee" }}
            />
            <CardContent>
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : filteredTutors.length > 0 ? (
                <ArgonBox>
                  {/* Select All Checkbox */}
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    p={2}
                    borderBottom="1px solid #eee"
                  >
                    <ArgonTypography variant="caption" color="text" fontWeight="medium" mr={1}>
                      Select All
                    </ArgonTypography>
                    <Checkbox
                      checked={
                        selectedTutorIds.length === filteredTutors.length &&
                        filteredTutors.length > 0
                      }
                      indeterminate={
                        selectedTutorIds.length > 0 &&
                        selectedTutorIds.length < filteredTutors.length
                      }
                      onChange={handleSelectAll}
                      color="primary"
                    />
                  </ArgonBox>
                  <ArgonBox
                    sx={{
                      "& .MuiTableRow-root:not(:last-child)": {
                        "& td": {
                          borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                            `${borderWidth[1]} solid ${borderColor}`,
                        },
                      },
                    }}
                  >
                    <Table columns={columns} rows={formatTutorsForTable()} />
                  </ArgonBox>
                </ArgonBox>
              ) : (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <ArgonTypography variant="body2" color="text">
                    No tutors found. Please apply filters to see results.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </CardContent>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
